import type { AppAction } from 'actions/types/ActionTypes'
import { SET_LOYALTY_ESIM_BENEFIT_LIST, UPDATE_LOYALTY_ESIM_CODE } from 'actions/actionConstants'
import requestLoyaltyEsimBenefits from 'api/loyalty/requestLoyaltyEsimBenefits'
import requestLoyaltyEsimOrderDetails from 'api/loyalty/requestLoyaltyEsimOrderDetails'
import pollEsimPromoCode from './pollEsimPromoCode'
import {
  formatEsimDate,
  formatEsimDateRange,
  formatEsimDestination,
  getEsimActivationDate,
  isPastEsimTripEnd,
  isWithinEsimRedemptionWindow,
} from 'loyalty/lib/loyaltyEsimUtils'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import { getCurrentUserId } from 'selectors/accountSelectors'
import { getCurrentIntl } from 'i18n/util/currentIntl'

function fetchEsimPromoCodes(orderIdFilter?: string): AppAction {
  /**
   * eSIM promo data flow:
   * 1. Load eligible benefits from /esim/orders/{orderId}/details (single) or /esim/orders (multi).
   * 2. Populate codesByOrderId with display metadata and lifecycle phase.
   * 3. For redeemable orders, call /esim/order/{orderId} to retrieve or generate the promocode.
   */
  return async(dispatch, getState) => {
    const state = getState()
    const accountRegion = getLoyaltyAccountRegion(state)
    if (!accountRegion) {
      if (!orderIdFilter) {
        dispatch({ type: SET_LOYALTY_ESIM_BENEFIT_LIST, orderIds: [] })
      }
      return
    }

    const intl = getCurrentIntl()
    const internationalTripLabel = intl.formatMessage({
      id: 'w2csFNE5b6Q3-38YywXP',
      defaultMessage: 'International trip',
      description: 'Fallback destination label for eligible eSIM orders when specific destination names are unavailable',
    })

    let benefits: Array<App.LoyaltyEsimBenefit> = []

    if (orderIdFilter) {
      // Fetch single order
      const order = state.orders.orders[orderIdFilter]
      if (!order) return

      try {
        const benefit = await requestLoyaltyEsimOrderDetails(orderIdFilter, order.customerId)
        // Per-order details can return CANCELLED; treat as ineligible, otherwise code will treat existence as eligible and attempt to poll for a code
        if (!benefit || benefit.status === 'CANCELLED') {
          dispatch({
            type: UPDATE_LOYALTY_ESIM_CODE,
            orderId: orderIdFilter,
            update: {
              orderId: orderIdFilter,
              destination: '',
              dates: '',
              phase: 'not_found',
            },
          })
          return
        }
        benefits = [benefit]
      } catch {
        dispatch({
          type: UPDATE_LOYALTY_ESIM_CODE,
          orderId: orderIdFilter,
          update: {
            orderId: orderIdFilter,
            destination: '',
            dates: '',
            phase: 'error',
          },
        })
        return
      }
    } else {
      // Fetch all orders
      const customerId = getCurrentUserId(state)
      if (!customerId) {
        dispatch({ type: SET_LOYALTY_ESIM_BENEFIT_LIST, orderIds: [] })
        return
      }

      try {
        benefits = await requestLoyaltyEsimBenefits(customerId)
      } catch {
        dispatch({
          type: SET_LOYALTY_ESIM_BENEFIT_LIST,
          orderIds: [],
        })
        return
      }
    }

    const eligibleBenefits = benefits.filter(benefit => {
      if (benefit.status === 'CANCELLED') return false
      const tripEnded = isPastEsimTripEnd(benefit.endDate)
      return orderIdFilter ? true : !tripEnded
    })

    if (!orderIdFilter) {
      dispatch({
        type: SET_LOYALTY_ESIM_BENEFIT_LIST,
        orderIds: eligibleBenefits.map(benefit => benefit.orderId),
      })
    }

    for (const benefit of eligibleBenefits) {
      const tripEnded = isPastEsimTripEnd(benefit.endDate)
      const isWithinRedemptionWindow = isWithinEsimRedemptionWindow(
        benefit.departureDate ?? undefined,
        benefit.endDate ?? undefined,
      )

      const destination = formatEsimDestination(benefit.countryCodes, intl.locale, internationalTripLabel)
      const dates = formatEsimDateRange(benefit.departureDate, benefit.endDate, intl.locale)
      const activationDate = getEsimActivationDate(benefit.departureDate, benefit.endDate)
      const availableDate = activationDate
        ? formatEsimDate(activationDate, intl.locale)
        : undefined

      const benefitFields = {
        orderId: benefit.orderId,
        destination,
        dates,
        tier: benefit.tier,
        packageSizeGB: benefit.packageSizeGB,
        unlimitedDays: benefit.unlimitedDays,
      }

      if (benefit.promocode) {
        dispatch({
          type: UPDATE_LOYALTY_ESIM_CODE,
          orderId: benefit.orderId,
          update: {
            ...benefitFields,
            phase: 'success',
            code: benefit.promocode,
          },
        })
        continue
      }

      const existing = getState().loyalty.esimPromoCodes.codesByOrderId[benefit.orderId]
      const shouldPreservePhase = existing
        && existing.phase !== 'not_found'
        && existing.phase !== 'error'
        && existing.phase !== 'booking_pending'
        && !(existing.phase === 'available_later' && isWithinRedemptionWindow)

      if (shouldPreservePhase) {
        dispatch({
          type: UPDATE_LOYALTY_ESIM_CODE,
          orderId: benefit.orderId,
          update: benefitFields,
        })
        continue
      }

      const pollContext = {
        orderId: benefit.orderId,
        customerId: benefit.customerId,
        tier: benefit.tier,
        tripEnded,
        availableDate,
      }

      if (tripEnded) {
        dispatch({
          type: UPDATE_LOYALTY_ESIM_CODE,
          orderId: benefit.orderId,
          update: {
            ...benefitFields,
            phase: 'loading',
          },
        })
        dispatch(
          pollEsimPromoCode(pollContext),
        ).catch(() => {
          dispatch({
            type: UPDATE_LOYALTY_ESIM_CODE,
            orderId: benefit.orderId,
            update: {
              ...benefitFields,
              phase: 'not_found',
            },
          })
        })
        continue
      }

      if (!isWithinRedemptionWindow) {
        dispatch({
          type: UPDATE_LOYALTY_ESIM_CODE,
          orderId: benefit.orderId,
          update: {
            ...benefitFields,
            phase: 'available_later',
            availableDate,
          },
        })
        continue
      }

      dispatch({
        type: UPDATE_LOYALTY_ESIM_CODE,
        orderId: benefit.orderId,
        update: {
          ...benefitFields,
          phase: 'loading',
        },
      })

      dispatch(
        pollEsimPromoCode(pollContext, { deferGeneratingCopy: true }),
      ).catch(() => {
        dispatch({
          type: UPDATE_LOYALTY_ESIM_CODE,
          orderId: benefit.orderId,
          update: { phase: 'error' },
        })
      })
    }
  }
}

export default fetchEsimPromoCodes
